import React, { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import {
  getServerConfig, updateServerConfig, testServerConnection,
  MailServerConfig, MailServerConfigUpdate,
} from '../services/mailService';
import { usePermission } from '../hooks/usePermission';
import { useMessage } from '../contexts/MessageContext';
import { validateSession } from '../utils/sessionValidator';
import FunctionPageHeader from '../components/FunctionPageHeader';
import { formatLocal } from '../utils/dateHelper';

const EEMailServerPage: React.FC = () => {
  const { t } = useTranslation();
  const { hasPermission, loading: permLoading } = usePermission();
  const { showSuccess, showApiError } = useMessage();

  const hasInit = useRef(false);

  const [config, setConfig] = useState<MailServerConfig | null>(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [form, setForm] = useState<MailServerConfigUpdate>({});
  const [testResult, setTestResult] = useState<{ connected: boolean; mail_api_url: string } | null>(null);

  const canUpdate = hasPermission('ee_mail_server', 'update');

  useEffect(() => {
    if (!permLoading && !hasInit.current) {
      hasInit.current = true;
      const init = async () => {
        const valid = await validateSession('EEMailServerPage');
        if (!valid) return;
        setLoading(true);
        try {
          const data = await getServerConfig();
          setConfig(data);
          setForm({
            mail_api_url: data.mail_api_url ?? '',
            webmail_url: data.webmail_url ?? '',
            is_active: data.is_active,
          });
        } catch (err) { showApiError(err); }
        finally { setLoading(false); }
      };
      init();
    }
  }, [permLoading]);

  const handleSave = async () => {
    setSaving(true);
    try {
      const payload: MailServerConfigUpdate = { ...form };
      // 若 mail_api_key 欄位空白，不送出（保留原值）
      if (!payload.mail_api_key) delete payload.mail_api_key;
      const updated = await updateServerConfig(payload);
      setConfig(updated);
      showSuccess('SYS020001', { name: t('mail.server.mailApiUrl') });
    } catch (err) { showApiError(err); }
    finally { setSaving(false); }
  };

  const handleTest = async () => {
    setTesting(true);
    setTestResult(null);
    try {
      const r = await testServerConnection();
      setTestResult(r);
    } catch (err) { showApiError(err); }
    finally { setTesting(false); }
  };

  if (loading) return <div style={{ padding: 24 }}>{t('common.loading')}</div>;

  return (
    <div>
      <FunctionPageHeader funcCode="ee_mail_server" />
      <div style={{ padding: '16px 24px', maxWidth: 640 }}>

        {/* Mail Agent URL */}
        <div style={{ marginBottom: 16 }}>
          <label style={{ display: 'block', fontWeight: 500, marginBottom: 4 }}>
            {t('mail.server.mailApiUrl')}
          </label>
          <input
            disabled={!canUpdate}
            value={form.mail_api_url ?? ''}
            onChange={e => setForm(f => ({ ...f, mail_api_url: e.target.value }))}
            placeholder="http://10.1.0.225:8089"
            style={{ width: '100%', padding: '8px 10px', border: '1px solid #ccc', borderRadius: 4 }}
          />
        </div>

        {/* Mail Agent API Key */}
        <div style={{ marginBottom: 16 }}>
          <label style={{ display: 'block', fontWeight: 500, marginBottom: 4 }}>
            {t('mail.server.mailApiKey')}
            {config?.mail_api_key_set && (
              <span style={{ marginLeft: 8, fontSize: 12, color: '#388e3c', fontWeight: 400 }}>
                ✓ 已設定
              </span>
            )}
          </label>
          <input
            type="password"
            disabled={!canUpdate}
            value={form.mail_api_key ?? ''}
            onChange={e => setForm(f => ({ ...f, mail_api_key: e.target.value }))}
            placeholder={config?.mail_api_key_set ? '（留空保留原值）' : '貼上 API Key（至少 32 字元）'}
            style={{ width: '100%', padding: '8px 10px', border: '1px solid #ccc', borderRadius: 4 }}
          />
        </div>

        {/* Webmail URL */}
        <div style={{ marginBottom: 16 }}>
          <label style={{ display: 'block', fontWeight: 500, marginBottom: 4 }}>
            {t('mail.server.webmailUrl')}
          </label>
          <input
            disabled={!canUpdate}
            value={form.webmail_url ?? ''}
            onChange={e => setForm(f => ({ ...f, webmail_url: e.target.value }))}
            placeholder="http://mail.yourdomain.com"
            style={{ width: '100%', padding: '8px 10px', border: '1px solid #ccc', borderRadius: 4 }}
          />
        </div>

        {/* Is Active */}
        <div style={{ marginBottom: 24, display: 'flex', alignItems: 'center', gap: 8 }}>
          <label style={{ fontWeight: 500 }}>{t('mail.server.isActive')}</label>
          <input
            type="checkbox"
            disabled={!canUpdate}
            checked={form.is_active ?? true}
            onChange={e => setForm(f => ({ ...f, is_active: e.target.checked }))}
          />
        </div>

        {/* Actions */}
        <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', alignItems: 'center' }}>
          {canUpdate && (
            <button
              onClick={handleSave}
              disabled={saving}
              style={{ padding: '8px 20px', background: '#1976d2', color: '#fff', border: 'none', borderRadius: 4, cursor: 'pointer' }}
            >
              {saving ? t('common.saving') : t('common.save')}
            </button>
          )}
          <button
            onClick={handleTest}
            disabled={testing}
            style={{ padding: '8px 20px', background: '#388e3c', color: '#fff', border: 'none', borderRadius: 4, cursor: 'pointer' }}
          >
            {testing ? t('common.loading') : t('mail.server.testConnection')}
          </button>
          {testResult && (
            <span style={{ color: testResult.connected ? '#388e3c' : '#d32f2f', fontWeight: 500 }}>
              {testResult.connected ? `✓ ${t('mail.server.connected')}` : `✗ ${t('mail.server.notConnected')}`}
              {' '}({testResult.mail_api_url})
            </span>
          )}
        </div>

        {config && (
          <p style={{ marginTop: 16, color: '#777', fontSize: 13 }}>
            {t('user.updatedAt')}: {config.updated_at ? formatLocal(config.updated_at) : '-'}
          </p>
        )}
      </div>
    </div>
  );
};

export default EEMailServerPage;